import { FC, Fragment, MutableRefObject } from 'react'
import classNames from 'classnames'
import { Dialog, Transition } from '@headlessui/react'

interface ModalProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
  onClose?: (value: boolean) => void
  staticBackdrop?: boolean
  /**
   * Elemento que recibe el foco al abrir. Sin esto, Headless UI enfoca el panel
   * y el operador tiene que agarrar el mouse para empezar a trabajar.
   */
  initialFocus?: MutableRefObject<HTMLElement | null>
  /**
   * Escape, INDEPENDIENTE del click en el backdrop.
   *
   * Headless UI enruta las dos cosas por el mismo `onClose`, y `staticBackdrop`
   * lo anula para que un click afuera no cierre un formulario a medio llenar.
   * Efecto colateral: también apaga Escape y el modal queda sin salida por
   * teclado. Este prop devuelve Escape sin devolver el click.
   */
  onEscape?: () => void
}

export const Modal: FC<ModalProps> = ({
  open,
  setOpen,
  className,
  style,
  children,
  onClose,
  staticBackdrop = false,
  initialFocus,
  onEscape,
}) => {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={initialFocus as MutableRefObject<HTMLElement> | undefined}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Escape' && onEscape) {
            e.preventDefault()
            e.stopPropagation()
            onEscape()
          }
        }}
        onClose={staticBackdrop ? () => {} : onClose || setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                style={style}
                className={classNames(
                  'relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:p-6',
                  className
                )}
              >
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
